import { useState, useCallback } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useToast } from '@/components/ToastContainer'
import { apiFetch } from '@/lib/api'
import {
  Input,
  Select,
  Button,
  Table,
  Modal,
  Sparkline,
  EmptyState,
  Spinner,
  ConfirmDialog,
  formatDate,
  formatCurrency,
  type TableColumn,
} from '@/ui'
import styles from './MarketingLinks.module.css'

// 목적지 select에서 "직접 입력"을 나타내는 센티넬 값
const MANUAL = '__manual__'

interface ChannelProduct {
  channelProductNo: string
  name: string
  salePrice: number
  storeUrl: string
}

interface ProductsResponse {
  products: ChannelProduct[]
  unavailable: boolean
}

interface MarketingLink {
  id: string
  label: string
  trackingCode: string
  shortUrl: string
  feedUrl: string | null
  destinationUrl: string
  utmSource: string
  utmMedium: string
  utmCampaign: string | null
  clickCount: number
  createdAt: string
}

interface ClickRow {
  clickedAt: string
  deviceType: string | null
  referer: string | null
}

// 링크별 귀속 퍼널 — 코호트 방식(가입은 기간 내, 결제는 그 코호트의 전 기간)
interface AttributionRow {
  id: string
  label: string
  trackingCode: string
  clickCount: number
  clicks: number
  signups: number
  payers: number
  netRevenue: number
  zeroAmountPayments: number
}

interface AttributionResponse {
  days: number | null
  rows: AttributionRow[]
}

// UTM 소스/캠페인별 신규가입 + 결제 귀속 (stats API)
interface UtmSummaryRow {
  source: string
  medium: string | null
  campaign: string | null
  signups: number
  payers: number
  netRevenue: number
}

interface UtmSummaryResponse {
  days: number
  rows: UtmSummaryRow[]
}

interface ClicksResponse {
  // 봇은 기록 단계에서 드롭되므로 전부 사람 클릭 (total = 사람 클릭 수)
  summary: { total: number; lastClickedAt: string | null }
  daily: { date: string; count: number }[]
  recent: ClickRow[]
}

// 링크별 클릭 기록 모달 — 요약 + 일별 추이 + 최근 클릭 목록
function ClicksModal({ link, onClose }: { link: MarketingLink; onClose: () => void }) {
  const { data, isLoading } = useApiFetch<ClicksResponse>(`/api/marketing-links/${link.id}/clicks`)

  const recentColumns: TableColumn<ClickRow>[] = [
    { key: 'clickedAt', label: '시각', render: (r) => formatDate(r.clickedAt, 'yyyy-MM-dd HH:mm') },
    { key: 'deviceType', label: '디바이스', render: (r) => r.deviceType ?? '-' },
    {
      key: 'referer',
      label: '유입',
      render: (r) => <span className={styles.dest}>{r.referer || '-'}</span>,
    },
  ]

  return (
    <Modal open onClose={onClose} title={`클릭 기록 — ${link.label}`} size="lg">
      {isLoading ? (
        <Spinner />
      ) : !data ? (
        <EmptyState title="기록을 불러올 수 없습니다" />
      ) : (
        <div className={styles.clicks}>
          <div className={styles.clicksSummary}>
            <span>클릭 <b>{data.summary.total}</b></span>
            <span>
              마지막 {data.summary.lastClickedAt ? formatDate(data.summary.lastClickedAt, 'yyyy-MM-dd HH:mm') : '-'}
            </span>
            {data.daily.length >= 2 && <Sparkline data={data.daily.map((d) => d.count)} width={160} height={28} />}
          </div>
          {data.recent.length === 0 ? (
            <EmptyState title="아직 클릭이 없습니다" />
          ) : (
            <Table data={data.recent} columns={recentColumns} />
          )}
        </div>
      )}
    </Modal>
  )
}

// UTM 소스/캠페인별 요약 테이블 — 최근 30일 가입 코호트의 결제 귀속
function UtmSummarySection() {
  const { data, isLoading } = useApiFetch<UtmSummaryResponse>('/api/stats/attribution?days=30')

  const columns: TableColumn<UtmSummaryRow>[] = [
    { key: 'source', label: '소스' },
    { key: 'medium', label: '매체', render: (r) => r.medium ?? '-' },
    { key: 'campaign', label: '캠페인', render: (r) => r.campaign ?? '-' },
    { key: 'signups', label: '가입' },
    { key: 'payers', label: '결제자' },
    { key: 'netRevenue', label: '순매출', render: (r) => formatCurrency(r.netRevenue) },
  ]

  return (
    <section className={styles.utmSummary}>
      <h3 className={styles.sectionTitle}>UTM 채널별 가입·매출 (최근 30일 가입 기준)</h3>
      {isLoading ? (
        <Spinner />
      ) : !data || data.rows.length === 0 ? (
        <EmptyState title="최근 30일 가입 데이터가 없습니다" />
      ) : (
        <Table data={data.rows} columns={columns} />
      )}
    </section>
  )
}

export function MarketingLinks() {
  const { addToast } = useToast()
  const { data, isLoading, refetch } = useApiFetch<MarketingLink[]>('/api/marketing-links')
  const { data: productData } = useApiFetch<ProductsResponse>('/api/marketing-links/products')
  const { data: attributionData } = useApiFetch<AttributionResponse>('/api/marketing-links/attribution')

  const [label, setLabel] = useState('')
  // productUrl: select 값 — 상품 storeUrl, MANUAL(직접 입력), 또는 '' (미선택)
  const [productUrl, setProductUrl] = useState('')
  const [manualUrl, setManualUrl] = useState('')
  const [feedUrl, setFeedUrl] = useState('')
  const [creating, setCreating] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [clicksLink, setClicksLink] = useState<MarketingLink | null>(null)

  const products = productData?.products ?? []
  const productsUnavailable = productData?.unavailable ?? false

  const destinationOptions = [
    {
      value: '',
      label: productsUnavailable
        ? '상품 목록을 불러올 수 없음 — 직접 입력하세요'
        : '목적지 상품 선택...',
    },
    ...products.map((p) => ({
      value: p.storeUrl,
      label: p.salePrice ? `${p.name} · ${p.salePrice.toLocaleString()}원` : p.name,
    })),
    { value: MANUAL, label: '+ 직접 입력' },
  ]

  const handleCreate = useCallback(async () => {
    if (creating) return
    const dest = (productUrl === MANUAL ? manualUrl : productUrl).trim()
    if (!label.trim() || !dest) {
      addToast({ type: 'error', message: '이름과 목적지 URL은 필수입니다' })
      return
    }
    setCreating(true)
    try {
      // admin의 apiFetch는 성공 시 data를 언랩해 반환하고 실패 시 throw 한다
      await apiFetch('/api/marketing-links', {
        method: 'POST',
        body: JSON.stringify({
          label: label.trim(),
          destinationUrl: dest,
          feedUrl: feedUrl.trim() || undefined,
        }),
      })
      addToast({ type: 'success', message: '링크 생성됨' })
      setLabel('')
      setProductUrl('')
      setManualUrl('')
      setFeedUrl('')
      refetch()
    } catch (e) {
      addToast({ type: 'error', message: e instanceof Error ? e.message : '링크 생성 실패' })
    } finally {
      setCreating(false)
    }
  }, [creating, label, productUrl, manualUrl, feedUrl, addToast, refetch])

  const handleCopy = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      addToast({ type: 'success', message: '복사됨' })
    } catch {
      addToast({ type: 'error', message: '복사 실패' })
    }
  }, [addToast])

  const handleDelete = useCallback(async (id: string) => {
    try {
      await apiFetch(`/api/marketing-links/${id}`, { method: 'DELETE' })
      addToast({ type: 'success', message: '삭제됨' })
      refetch()
    } catch (e) {
      addToast({ type: 'error', message: e instanceof Error ? e.message : '삭제 실패' })
    }
  }, [addToast, refetch])

  // 링크 id → 귀속 퍼널 행 매핑 (attribution 응답이 아직 없으면 '-' 표시)
  const attributionById = new Map(
    (attributionData?.rows ?? []).map((r) => [r.id, r]),
  )

  const columns: TableColumn<MarketingLink>[] = [
    { key: 'label', label: '이름' },
    {
      key: 'shortUrl',
      label: '단축링크',
      render: (r) => (
        <button type="button" className={styles.copyBtn} onClick={() => handleCopy(r.shortUrl)}>
          {r.shortUrl} 📋
        </button>
      ),
    },
    {
      key: 'feedUrl',
      label: '인스타 게시물',
      render: (r) => (r.feedUrl ? <a href={r.feedUrl} target="_blank" rel="noreferrer">열기</a> : <span>-</span>),
    },
    { key: 'destinationUrl', label: '목적지', render: (r) => <span className={styles.dest}>{r.destinationUrl}</span> },
    { key: 'clickCount', label: '클릭' },
    {
      key: 'signups',
      label: '가입',
      render: (r) => attributionById.get(r.id)?.signups ?? '-',
    },
    {
      key: 'payers',
      label: '결제자',
      render: (r) => attributionById.get(r.id)?.payers ?? '-',
    },
    {
      key: 'netRevenue',
      label: '순매출',
      render: (r) => {
        const a = attributionById.get(r.id)
        if (!a) return '-'
        return (
          <span>
            {formatCurrency(a.netRevenue)}
            {a.zeroAmountPayments > 0 && (
              <span
                className={styles.revenueCaveat}
                title="스마트스토어 활성화 코드 결제 일부는 금액이 0으로 기록되어 순매출에 반영되지 않습니다"
              >
                *
              </span>
            )}
          </span>
        )
      },
    },
    { key: 'createdAt', label: '생성일', render: (r) => formatDate(r.createdAt) },
    {
      key: 'id',
      label: '',
      render: (r) => (
        <div className={styles.rowActions}>
          <Button size="sm" variant="outline" onClick={() => setClicksLink(r)}>기록</Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDeleteId(r.id)}>삭제</Button>
        </div>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.form}>
        <Input placeholder="이름 (예: 6/3 드레스 콘텐츠)" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Select
          value={productUrl}
          onChange={(e) => setProductUrl(e.target.value)}
          options={destinationOptions}
        />
        {productUrl === MANUAL && (
          <Input
            placeholder="목적지 URL 직접 입력 (smartstore.naver.com/...)"
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
          />
        )}
        <Input placeholder="인스타 게시물 URL (선택)" value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} />
        <Button onClick={handleCreate} disabled={creating}>링크 생성</Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : !data || data.length === 0 ? (
        <EmptyState title="아직 링크가 없습니다" />
      ) : (
        <Table data={data} columns={columns} />
      )}

      <UtmSummarySection />

      <ConfirmDialog
        open={pendingDeleteId !== null}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={async () => {
          if (pendingDeleteId) await handleDelete(pendingDeleteId)
          setPendingDeleteId(null)
        }}
        title="링크 삭제"
        message="이 링크를 삭제하시겠어요? 이미 인스타에 붙인 단축링크는 동작하지 않게 됩니다."
        variant="danger"
      />

      {clicksLink && <ClicksModal link={clicksLink} onClose={() => setClicksLink(null)} />}
    </div>
  )
}
